// =============================================================================
// Geometry of the logo, without "use client": shared by the client logos and
// the images rendered on the server (Open Graph…)
// =============================================================================

/**
 * "bc" monogram, exact geometry of the Figma "LOGO_GRADIENT_MIEUX" / app icon:
 * the stem and bowl of a "b" wrapping a "c". The top quarter of the bowl and
 * the bottom of the "c" fade out. Paths are ordered for a natural drawing
 * (stem from the top, then the bowl, then the "c").
 */
export const LOGO_PATHS = {
  stemAndBowl:
    "M159 72V308.583C159 362.385 202.615 406 256.417 406C310.218 406 353.833 362.385 353.833 308.583",
  bowlTop: "M353.842 308.582C353.842 254.78 310.228 211.165 256.426 211.165",
  innerTop: "M293.803 270.321C260.773 239.952 206.365 264.862 206.346 308.679",
  innerBottom:
    "M206.339 308.681C206.319 354.548 265.095 378.598 296.889 344.018",
} as const;

export type LogoPath = keyof typeof LOGO_PATHS;

/** Length of each path (getTotalLength), in viewBox units */
export const LOGO_PATH_LENGTHS: Record<LogoPath, number> = {
  stemAndBowl: 542.7,
  bowlTop: 153,
  innerTop: 120,
  innerBottom: 124.4,
};

/** The "b" in one stroke (stem, bowl, top of the bowl), then the "c" */
const DRAW_ORDER: LogoPath[] = [
  "stemAndBowl",
  "bowlTop",
  "innerTop",
  "innerBottom",
];

/**
 * Share of a drawing progress (from start to end) given to each path, in
 * proportion to its length: the paths are drawn one after the other at the
 * same speed, so a line only appears once the pen reaches it.
 */
export function logoDrawRanges(
  start = 0,
  end = 1,
  lengths: Record<LogoPath, number> = LOGO_PATH_LENGTHS
): Record<LogoPath, [number, number]> {
  const total = DRAW_ORDER.reduce((sum, key) => sum + lengths[key], 0);
  const ranges = {} as Record<LogoPath, [number, number]>;
  let at = start;
  for (const key of DRAW_ORDER) {
    const next = at + ((end - start) * lengths[key]) / total;
    ranges[key] = [at, next];
    at = next;
  }
  return ranges;
}

export const LOGO_VIEWBOX = "148 61 217 356";
export const LOGO_OUTER_STROKE = 21.7826;
export const LOGO_INNER_STROKE = 14.5217;

// =============================================================================
